import React, { useState, useEffect, useRef } from 'react';
import Button from '../controls/forms/Button';
import Select from '../controls/forms/Select';
import { SelectItem } from 'src/models/common/SelectItem';
import useDebounce from 'src/util/hooks/use-debounce';
import { ODataOptions, ODataOrderByDirection, ODataFilter, ODataFilterOperators } from 'src/util/ODataBuilder';
import * as _ from 'lodash';

export type FilterAction = {
    text: string;
    action: () => void;
};

interface IFilterProps {
    sortItems?: FilterOrderItem[];
    filterItems?: FilterOrderItem[];
    actions?: FilterAction[];
    onFilterChanged?: (options: ODataOptions) => void;
    // omniSearchFields?: Array<string>;
    showFilterIcon?: boolean;
}

export type FilterOrderItem = {
    group: number;
    text: string;
    icon?: 'numeric-up' | 'numeric-down' | 'alpha-up' | 'alpha-down' | 'amount-up' | 'amount-down';
    orderName: string;
    orderDirection: 'ascending' | 'descending';
    searchCriteria?: string;
    type?: 'date' | 'text' | 'number' | 'topic';
};

const Filter: React.FC<IFilterProps> = (props) => {
    const [currentSort, setCurrentSort] = useState<FilterOrderItem>(null);
    const [currentFilter, setCurrentFilter] = useState<Array<FilterOrderItem>>([]);
    const [currentSearchTerm, setCurrentSearchTerm] = useState('');
    const [currentField, setCurrentField] = useState<FilterOrderItem>(null);
    const [currentFilterTerm, setCurrentFilterTerm] = useState('');
    const [type, setType] = useState<string>('');

    const debouncedCurrentSearchTerm = useDebounce(currentSearchTerm, 500);

    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const options = new ODataOptions();

        if (currentSort) {
            options.addOrderBy(
                currentSort.orderName,
                currentSort.orderDirection === 'ascending' ? ODataOrderByDirection.Ascending : ODataOrderByDirection.Descending
            );
        }

        if (debouncedCurrentSearchTerm) {
            options.addFilter(new ODataFilter('omni', ODataFilterOperators.Contains, debouncedCurrentSearchTerm));
            // options.addOmniSearch(props.omniSearchFields, debouncedCurrentSearchTerm);
        }

        if (currentFilter?.length > 0) {
            currentFilter.map((filter) => {
                options.addFilter(new ODataFilter('where', ODataFilterOperators.Equals, `${filter.orderName},${filter.searchCriteria}`));
            });
        }

        if (props.onFilterChanged) {
            props.onFilterChanged(options);
        }
    }, [currentSort, debouncedCurrentSearchTerm, currentFilter]);

    const showActions = () => {
        if (props.actions && props.actions.length > 0) {
            return props.actions.map((action: FilterAction, index: number) => (
                <Button size="small" key={`a-${index}`} text={action.text} onClick={action.action} />
            ));
        }

        return undefined;
    };

    const sortItems = () => {
        if (props.sortItems) {
            return props.sortItems.map((item: FilterOrderItem, index: number) => new SelectItem(index + 1, item.text, item.icon as string));
        }
        return undefined;
    };

    const filterItems = () => {
        if (props.filterItems) {
            return props.filterItems.map((item: FilterOrderItem, index: number) => new SelectItem(index + 1, item.text, item.icon as string));
        }
        return undefined;
    };

    const changeSort = (item: SelectItem) => {
        const itemsMatch = props.sortItems.filter((propsItem: FilterOrderItem) => propsItem.text === item.name);

        if (itemsMatch && itemsMatch.length > 0) {
            setCurrentSort(itemsMatch[0]);
        }
    };

    const changeFilter = () => {
        if (currentFilterTerm && currentFilterTerm.length > 0 && currentField) {
            currentField.searchCriteria = currentFilterTerm;
            setCurrentFilter([...currentFilter, currentField]);
        } else if (type === 'topic' && currentField) {
            let topicField: FilterOrderItem = _.cloneDeep(currentField);
            topicField.searchCriteria = topicField.text;
            topicField.text = 'Topic';
            setCurrentFilter([...currentFilter, topicField]);
        }
    };

    const setFilter = (item: SelectItem) => {
        const itemsMatch = props.filterItems.filter((propsItem: FilterOrderItem) => propsItem.text === item.name)[0];

        if (itemsMatch && itemsMatch) {
            setCurrentField(itemsMatch);
        }
        if (!!itemsMatch.type) {
            setType(itemsMatch.type);
        } else {
            setType('');
        }
    };

    const sortPanel = () => {
        if (!currentSort) {
            return <span>No sort order selected</span>;
        } else {
            return (
                <span className="filter-badge" onClick={() => setCurrentSort(null)}>
                    {currentSort.text}
                </span>
            );
        }
    };

    const removeFilterItem = (index: number) => {
        if (currentFilter?.length > 0) {
            currentFilter.splice(index, 1);
            setCurrentFilter([...currentFilter]);
        } else {
            setCurrentFilter([]);
        }
    };

    const filterPanel = () => {
        if (currentFilter?.length > 0) {
            return currentFilter.map((filter, index) => {
                return (
                    <span className="filter-badge" onClick={() => removeFilterItem(index)} key={`filter${index}`}>
                        {filter.text} - {filter.searchCriteria}
                    </span>
                );
            });
        } else {
            return <span>No filter selected</span>;
        }
    };

    const focusInput = () => {
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };
    return (
        <div className="dlc-filter-panel">
            <div className="icon-panel" style={{ display: props.showFilterIcon ? 'block' : 'none ' }}>
                <i className="fas fa-filter" />
            </div>
            <div className="options-panel">
                <div style={{ display: 'flex' }}>
                    <div style={{ display: 'flex', width: '50%' }}>
                        {props?.sortItems?.length > 0 && (
                            <>
                                <Select
                                    label="Sort"
                                    selectStyle="link"
                                    items={sortItems()}
                                    onChange={changeSort}
                                    style={{
                                        display: 'inline-block',
                                        marginBottom: '.5rem',
                                        borderBottom: '2px solid #6393C1',
                                        marginRight: '1rem',
                                        fontWeight: 'bold',
                                        color: '#6393C1',
                                        fontSize: '1.1em',
                                    }}
                                />
                            </>
                        )}

                        <div
                            style={{
                                borderBottom: '2px solid #6393C1',
                                height: '3.5rem',
                                padding: '0 1rem',
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                            onClick={focusInput}
                        >
                            <i
                                className="fas fa-search"
                                style={{ color: '#6393C1', position: 'relative', top: '1rem', marginRight: '1rem', fontSize: '1.2em' }}
                            />
                            <input
                                ref={inputRef}
                                onChange={(e) => setCurrentSearchTerm(e.target.value)}
                                style={{
                                    background: 'transparent',
                                    border: '0',
                                    maxHeight: '3rem',
                                    position: 'relative',
                                    top: '.3rem',
                                    bottom: '0',
                                    outline: 'none',
                                    fontSize: '1.1em',
                                    fontWeight: 'bold',
                                    color: '#6393C1',
                                }}
                            />
                        </div>
                    </div>
                    {props?.filterItems?.length > 0 && (
                        <div 
                            className="right-align" 
                            style={{
                                fontWeight: 'bold',
                                color: '#6393C1',
                                fontSize: '1.1em'
                            }}
                        >
                            <Select
                                label="Filter"
                                selectStyle="link"
                                items={filterItems()}
                                onChange={setFilter}
                                // tslint:disable-next-line: max-line-length
                                style={{
                                    marginBottom: '.5rem',
                                    borderBottom: '2px solid #6393C1',
                                    marginRight: '1rem',
                                }}
                            />
                            <div>
                                Field: {currentField?.text}
                            </div>
                            {type !== 'date' && type !== 'topic' && (
                                <div
                                    style={{
                                        borderBottom: '2px solid #6393C1',
                                        height: '3.5rem',
                                        padding: '0 1rem',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    <input
                                        onChange={(e) => setCurrentFilterTerm(e.target.value)}
                                        style={{
                                            background: 'transparent',
                                            border: '0',
                                            maxHeight: '3rem',
                                            position: 'relative',
                                            top: '.3rem',
                                            bottom: '0',
                                            outline: 'none',
                                        }}
                                    />
                                </div>
                            )}
                            {type === 'date' && (
                                <div
                                    style={{
                                        borderBottom: '2px solid #6393C1',
                                        height: '3.5rem',
                                        padding: '0 1rem',
                                        justifyContent: 'flex-end',
                                    }}
                                >
                                    <input
                                        onChange={(e) => setCurrentFilterTerm(e.target.value)}
                                        style={{
                                            background: 'transparent',
                                            border: '0',
                                            maxHeight: '3rem',
                                            position: 'relative',
                                            top: '.3rem',
                                            bottom: '0',
                                            outline: 'none',
                                        }}
                                        type="date"
                                    />
                                </div>
                            )}
                            <div className="mr-2" style={{marginLeft: '1rem'}}>
                                <Button 
                                    size="small" 
                                    text="Search" 
                                    onClick={changeFilter} 
                                    disabled={!((type === 'topic' && currentField) || (currentField && currentFilterTerm && currentFilterTerm.length > 0))}
                                />
                            </div>
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginRight: '1rem' }}>
                    {props?.sortItems?.length > 0 && (
                        <>
                            <div style={{ height: '2rem' }}>{sortPanel()}</div>
                        </>
                    )}
                    {props?.filterItems?.length > 0 && <div style={{ height: '2rem' }}>{filterPanel()}</div>}
                </div>
            </div>
            <div className="action-panel">{showActions()}</div>
        </div>
    );
};

Filter.defaultProps = {
    showFilterIcon: true,
};

export default Filter;



// WEBPACK FOOTER //
// ./src/components/common/Filter.tsx